import { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { useAuth } from '@contexts/User.context';
import { useToast } from '@contexts/Toast.context';
import useNotify from '@hooks/useNotify';
import { PROJECT_MODE } from '@utils/constants';
import { createProject, archiveProject, bulkUploadImagesToS3 } from '@utils/api';
import { startPostProcessing } from '@utils/api';

const useSubmitProject = () => {
	const { addToast } = useToast();
	const navigate = useNavigate();
	const { currentUser } = useAuth();
	const { checkForNotifications } = useNotify();
	const { publish_wms } = currentUser ?? {};
	const { ORTHOPHOTO, SINGLE_IMAGE } = PROJECT_MODE;

	const projectUuid = useRef(null);
	const [progress, setProgress] = useState(-1);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	const clearError = () => setError(null);
	const confirmUnload = e => e.preventDefault();

	const { mutateAsync: createProjectMutation } = useMutation({
		mutationFn: createProject,
		onMutate: () => console.log('%c 🛠️ Creating Project', 'color: #198754'),
		onError: err => console.error(`Error creating project: ${err}`),
	});

	const { mutateAsync: uploadFilesMutation } = useMutation({
		mutationFn: bulkUploadImagesToS3,
		onMutate: () => console.group('%c☁️ Uploading files', 'color: #0d6efd'),

		onError: err => console.error(`Error uploading files: ${err}`),
		onSettled: () => console.groupEnd(),
	});

	const { mutateAsync: startPostProcessingMutation } = useMutation({
		mutationFn: startPostProcessing,
		onMutate: () => console.log('%c⚙️ Starting post processing', 'color: #D49DFF'),

		onError: err => console.error(`Error starting post processing: ${err}`),
		onSuccess: () => console.log('%c✅ Post processing started', 'color: #00d26a'),
	});

	const { mutateAsync: archiveProjectMutation } = useMutation({
		mutationFn: archiveProject,
		onMutate: () =>
			console.log(
				'%c📦 Archiving project due to error in project creation',
				'color: #FF5722'
			),

		onError: err =>
			console.error(`Error archiving project when failing on project creation: ${err}`),
	});

	const submit = async ({ projectInfo, fileInfo, advancedOptions }) => {
		try {
			const { projectMode, projectName, projectDescription, projectTags } = projectInfo ?? {};
			const { files, isTif, totalFilesSize, exifData } = fileInfo ?? {};

			clearError();
			if (projectMode == ORTHOPHOTO && !isTif && files.length < 25)
				return addToast({
					id: `orthophoto-min-images-warning`,
					title: 'You need to upload at least 25 images to create an orthophoto project.',
					bg: 'warning',
				});

			if (projectMode == SINGLE_IMAGE && files.length < 1)
				return addToast({
					id: `single-image-min-images-warning`,
					title: 'You need to upload at least 1 image to create a single image project.',
					bg: 'warning',
				});

			setIsLoading(true);

			// Show confirmation dialog when user tries to leave the page
			window.addEventListener('beforeunload', confirmUnload);

			const { uuid } = await createProjectMutation({
				title: projectName,
				description: projectDescription,
				tags: projectTags,
				file_storage: totalFilesSize,
				orthophoto_project: projectMode == ORTHOPHOTO,
				wms_publish: publish_wms,
			});
			projectUuid.current = uuid;

			await uploadFilesMutation({ files, projectId: uuid, setProgress });

			// FIX: This check happens too early. Temporary fix is to comment it out, and trust that the totalFilesSize set abov is correct. A fix should be implemented in the future.
			// await updateProjectWithS3Usage(projectId, isOrthophoto);

			await startPostProcessingMutation({
				projectUuid: uuid,
				projectMode: projectMode,
				advancedOptions: advancedOptions,

				// File Info
				fileCount: files.length,
				exifData: exifData,
				tifSize: isTif ? files[0].size : null,
				isTif: isTif,
			});

			checkForNotifications();

			// Remove confirmation dialog when user leaves the page
			window.removeEventListener('beforeunload', confirmUnload);

			navigate(`/projects`);
		} catch (err) {
			// Remove confirmation dialog when user leaves the page
			window.removeEventListener('beforeunload', confirmUnload);

			console.group('%c❌ Error creating project.', 'color: #f92f60');
			console.error(err);
			console.groupEnd();

			const { current: uuid } = projectUuid;
			if (uuid) await archiveProjectMutation(uuid);
			setError(err);
		} finally {
			// Reset
			projectUuid.current = null;
			setIsLoading(false);
			setProgress(-1);
		}
	};

	return {
		isLoading,
		error,
		clearError,
		progress,
		submit,
	};
};

export default useSubmitProject;
